import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionCard, Bar, Empty } from "../components/shared";
import { inr, pct } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Wallet, Check } from "lucide-react";
import ExportMenu from "../components/ExportMenu";

export default function Budget() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const canEdit = user?.role === "admin" || user?.role === "finance";

  const load = () => api.get("/budget").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    await api.put(`/budget/${id}`, { annual_budget: Number(edit[id]) });
    toast.success("Budget updated"); setEdit((e) => { const n = { ...e }; delete n[id]; return n; }); load();
  };

  const totalBudget = rows.reduce((s, r) => s + r.annual_budget, 0);
  const totalSpent = rows.reduce((s, r) => s + r.spent, 0);

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Budget vs Actual Tracker</h1>
          <p className="text-brand-slate text-sm mt-1">Category-wise annual budget consumption · FY 2026 {canEdit ? "" : "(view only)"}</p>
        </div>
        <ExportMenu dataset="budget" testid="export-budget" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-border p-6"><div className="text-xs uppercase tracking-widest text-brand-slate font-semibold">Total Annual Budget</div><div className="font-heading text-3xl font-bold text-navy mt-2">{inr(totalBudget)}</div></div>
        <div className="bg-white rounded-xl border border-border p-6"><div className="text-xs uppercase tracking-widest text-brand-slate font-semibold">Spent to Date</div><div className="font-heading text-3xl font-bold text-brand-teal mt-2">{inr(totalSpent)}</div></div>
        <div className="bg-white rounded-xl border border-border p-6"><div className="text-xs uppercase tracking-widest text-brand-slate font-semibold">Remaining</div><div className="font-heading text-3xl font-bold text-brand-emerald mt-2">{inr(totalBudget - totalSpent)}</div></div>
      </div>

      <SectionCard title="Category Budgets">
        {rows.length === 0 ? <Empty icon={Wallet} msg="No budget set" /> :
          <div className="space-y-4">
            {rows.map((r) => (
              <div key={r.id} data-testid="budget-row" className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy">{r.category}</span>
                    {r.over_warning && <span className="flex items-center gap-1 text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> {pct(r.pct_consumed)} used</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-brand-slate">Spent <span className="font-semibold text-navy tabular-nums">{inr(r.spent)}</span></span>
                    {canEdit && edit[r.id] !== undefined ? (
                      <div className="flex items-center gap-1">
                        <Input type="number" value={edit[r.id]} onChange={(e) => setEdit((s) => ({ ...s, [r.id]: e.target.value }))} className="h-8 w-32" />
                        <Button size="sm" onClick={() => save(r.id)} className="h-8 bg-brand-teal hover:bg-brand-tealdark px-2"><Check className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <button data-testid="budget-edit" disabled={!canEdit} onClick={() => setEdit((s) => ({ ...s, [r.id]: r.annual_budget }))} className={`tabular-nums ${canEdit ? "text-brand-teal hover:underline" : "text-navy cursor-default"}`}>
                        Budget {inr(r.annual_budget)}
                      </button>
                    )}
                  </div>
                </div>
                <Bar value={r.pct_consumed} warn={r.over_warning} />
                <div className="flex justify-between text-xs text-brand-slate"><span>{pct(r.pct_consumed)} consumed</span><span>Remaining {inr(r.remaining)}</span></div>
              </div>
            ))}
          </div>}
      </SectionCard>
    </div>
  );
}
